import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ChevronRight, ChevronUp, Sparkles } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { RecommendationSummary } from '@/components/custom/deal-review/review-controls'
import { cn } from '@/lib/utils'
import { showError } from '@/lib/toast'
import {
  useDealDecisionOptions,
  useRecordDealDecision,
} from '@/hooks/use-deal-leads'
import {
  decisionDiffers,
  saveableDispositionOrNull,
} from '@/features/deal-review/review-options'
import {
  dispositionLabel,
  screeningOutcomeLabel,
} from '@/features/deal-review/deal-lead-labels'
import type { DealLeadDetail, RecordDealDecision } from '@/types/api'

// UNREVIEWED is a starting state the server refuses.
type SaveableDisposition = RecordDealDecision['disposition']

function saveableOrNull(value: string | undefined): SaveableDisposition | null {
  return value === undefined ? null : saveableDispositionOrNull(value)
}

// Long enough to collapse a changed mind into one decision, short enough not to be outrun.
const AUTOSAVE_DELAY_MS = 1200

const ADD_ON_VALUES = { yes: 'yes', no: 'no', unset: 'unset' } as const
type AddOnValue = (typeof ADD_ON_VALUES)[keyof typeof ADD_ON_VALUES]

function addOnToValue(confirmed: boolean | undefined): AddOnValue {
  if (confirmed === undefined) return ADD_ON_VALUES.unset
  return confirmed ? ADD_ON_VALUES.yes : ADD_ON_VALUES.no
}

// Solid means on the record; dashed + sparkle is screening's proposal, never a recorded decision.
// #166534 is green-800: the Pass tone has no token.
const CHOICE_PAINT: Record<
  SaveableDisposition,
  { recorded: string; proposed: string; idle: string }
> = {
  PURSUE: {
    recorded:
      'border-green-800 bg-green-800 font-bold text-white hover:bg-green-800 hover:text-white',
    proposed:
      'border-dashed border-green-800 bg-green-50 font-bold text-green-800 hover:bg-green-50 hover:text-green-800',
    idle: 'border-green-800/35 bg-white font-normal text-green-800 hover:bg-green-50 hover:text-green-800',
  },
  PASS: {
    recorded:
      'border-destructive bg-destructive font-bold text-white hover:bg-destructive hover:text-white',
    proposed:
      'border-dashed border-destructive bg-destructive/5 font-bold text-destructive hover:bg-destructive/5 hover:text-destructive',
    idle: 'border-destructive/35 bg-white font-normal text-destructive hover:bg-destructive/5 hover:text-destructive',
  },
}

const FIELD_CHROME =
  'h-8.5 w-full rounded-lg border-primary/55 bg-white px-2.5 text-sm'

interface FormState {
  disposition: SaveableDisposition | null
  reason: string | null
  commentary: string
  addOn: AddOnValue
}

function initialState(lead: DealLeadDetail): FormState {
  const run = lead.latestScreeningRun
  const decided = lead.disposition !== 'UNREVIEWED'

  return {
    disposition: saveableOrNull(
      decided ? lead.disposition : run?.recommendedDisposition
    ),
    reason: decided ? (lead.dispositionReason ?? null) : null,
    // Not gated on a decision: a note can be saved on an undecided lead.
    commentary: lead.dispositionCommentary ?? '',
    addOn: decided
      ? addOnToValue(lead.addOnConfirmed)
      : addOnToValue(run ? run.addOnProposed : undefined),
  }
}

// Every writable field: a reason can change on a lead whose disposition did not.
function recordSignature(lead: DealLeadDetail): string {
  return [
    lead.disposition,
    lead.dispositionReason ?? '',
    lead.dispositionCommentary ?? '',
    String(lead.addOnConfirmed ?? ''),
  ].join('|')
}

function screeningHeadline(lead: DealLeadDetail): {
  sentence: string
  tail: string | null
} {
  const run = lead.latestScreeningRun
  if (!run) {
    return { sentence: 'This lead has not been screened yet.', tail: null }
  }

  const count = (outcome: string) =>
    run.criterionResults.filter((result) => result.outcome === outcome).length
  const failed = count('FAIL')
  const undecided = count('UNKNOWN')
  const passed = count('PASS')
  const notApplicable = count('NOT_APPLICABLE')

  if (run.criterionResults.length === 0) {
    return {
      sentence: `Screening suggests ${dispositionLabel(run.recommendedDisposition)}.`,
      tail: `Core result: ${screeningOutcomeLabel(run.coreResult)}.`,
    }
  }

  return {
    sentence: `Screening suggests ${dispositionLabel(run.recommendedDisposition)}.`,
    tail:
      (failed === 0 ? 'Nothing failed, ' : `${failed} failed, `) +
      `${undecided} could not be decided, ${passed} met` +
      (notApplicable > 0 ? `, ${notApplicable} not applicable.` : '.'),
  }
}

// The caller remounts this per lead, so seeded state always matches the row on screen.
export function DealLeadDecisionForm({
  lead,
  onDecided,
  collapsed = false,
  onCollapsedChange,
}: {
  lead: DealLeadDetail
  onDecided?: (detail: DealLeadDetail) => void
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
}) {
  const options = useDealDecisionOptions()
  const save = useRecordDealDecision(lead.id)

  const [form, setForm] = useState<FormState>(() => initialState(lead))
  const [detailOpen, setDetailOpen] = useState(
    () => initialState(lead).commentary.length > 0
  )
  // Set by the controls, never by diffing: the seeded recommendation would autosave as a person's.
  const [touched, setTouched] = useState(false)
  // Only committed text is saved, so a sentence isn't persisted and audited mid-word.
  const [committedCommentary, setCommittedCommentary] = useState(
    () => initialState(lead).commentary
  )

  // Re-seeds when the record changes elsewhere, unless the reviewer holds unsaved or typed edits.
  // Set during render rather than in an effect, so the stale draft is never painted.
  const signature = recordSignature(lead)
  const [seededFrom, setSeededFrom] = useState(signature)

  // NEEDS_INFORMATION is a screening conclusion, not a reviewer call, so the guard drops it.
  const choices = useMemo(
    () =>
      (options.data?.dispositions ?? [])
        .map((disposition) => saveableOrNull(disposition))
        .filter((disposition) => disposition !== null),
    [options.data]
  )

  const reasonRequired = useMemo(() => {
    if (form.disposition === null) return false
    return (options.data?.dispositionsRequiringReason ?? []).includes(
      form.disposition
    )
  }, [form.disposition, options.data])

  // Validated: DealCloud can retire the reason screening's criterion maps to.
  const recommendedReason = useMemo(() => {
    if (lead.disposition !== 'UNREVIEWED') return null
    const suggested = lead.recommendedPassReason
    if (suggested === undefined) return null
    const known = (options.data?.passReasons ?? []).some(
      (reason) => reason.value === suggested
    )
    return known ? suggested : null
  }, [lead.disposition, lead.recommendedPassReason, options.data])

  const effectiveReason = form.reason ?? recommendedReason

  const missingDisposition = form.disposition === null
  const missingReason = reasonRequired && !effectiveReason

  const differsFromRecord = decisionDiffers(
    {
      disposition: lead.disposition,
      reason: lead.dispositionReason ?? null,
      commentary: lead.dispositionCommentary ?? '',
      addOnConfirmed: lead.addOnConfirmed ?? null,
    },
    {
      disposition: form.disposition,
      reason: effectiveReason,
      commentary: form.commentary,
      addOnConfirmed:
        form.addOn === ADD_ON_VALUES.unset
          ? null
          : form.addOn === ADD_ON_VALUES.yes,
    }
  )
  if (signature !== seededFrom) {
    setSeededFrom(signature)
    const holding = touched && differsFromRecord
    const typing = form.commentary !== committedCommentary
    if (!holding && !typing) {
      const seeded = initialState(lead)
      setForm(seeded)
      setCommittedCommentary(seeded.commentary)
      setTouched(false)
    }
  }

  const canSubmit =
    lead.capabilities.canDecide &&
    !save.isPending &&
    !missingDisposition &&
    !missingReason

  const disposition = form.disposition
  const commentary = committedCommentary
  const addOnConfirmed =
    form.addOn === ADD_ON_VALUES.unset ? null : form.addOn === ADD_ON_VALUES.yes
  const { mutate } = save
  // Flushed on unmount, so closing or stepping to the next lead inside the delay still saves.
  const pendingWrite = useRef<(() => void) | null>(null)
  useEffect(() => () => pendingWrite.current?.(), [])

  // Debounced: every write is an audited judgement compared against the model.
  // The write lives in the effect: a callback would change identity and restart the timer.
  useEffect(() => {
    pendingWrite.current = null
    if (!touched || !canSubmit || !differsFromRecord) return
    if (disposition === null) return

    const write = () => {
      pendingWrite.current = null
      mutate(
        {
          // The server rejects a stale version rather than overwriting.
          version: lead.version,
          disposition,
          // null, not omitted, so clearing a reason on a re-decision actually clears it.
          reason: effectiveReason,
          commentary: commentary.trim() === '' ? null : commentary,
          addOnConfirmed,
          // platformCompanyId is never sent: the server rejects a company on a non-add-on lead.
        },
        {
          // No success toast: autosave fires as a reviewer works, and the save line reports it.
          onSuccess: onDecided,
          // Clear touched on error, or canSubmit settling re-arms the timer and saves in a loop.
          onError: (error) => {
            setTouched(false)
            showError(error, 'The decision could not be saved')
          },
        }
      )
    }
    pendingWrite.current = write
    const timer = setTimeout(write, AUTOSAVE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [
    touched,
    canSubmit,
    differsFromRecord,
    disposition,
    effectiveReason,
    commentary,
    addOnConfirmed,
    lead.version,
    mutate,
    onDecided,
  ])

  if (options.isError) {
    return (
      <div className="mx-5.5 rounded-[12px] bg-sidebar p-4">
        <Typography variant="body-sm" className="text-destructive">
          The decision options could not be loaded, so this cannot be shown
          without guessing the rules it has to follow.
        </Typography>
      </div>
    )
  }

  const headline = screeningHeadline(lead)
  const recorded = lead.disposition === 'UNREVIEWED' ? null : lead.disposition
  const disabled = !lead.capabilities.canDecide || save.isPending

  const unsaved =
    (touched && differsFromRecord) ||
    form.commentary.trim() !== committedCommentary.trim()
  const noBadge = lead.disposition === 'UNREVIEWED' && form.disposition === null
  const needsAttention =
    !lead.capabilities.canDecide || save.isPending || unsaved || noBadge

  const saveState = (
    <SaveState
      lead={lead}
      pending={save.isPending}
      unsaved={unsaved}
      missingReason={missingReason}
      selected={form.disposition}
    />
  )

  const fold = onCollapsedChange ? (
    <button
      type="button"
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Expand the decision' : 'Collapse the decision'}
      className="-m-1 shrink-0 rounded-md p-1 text-primary/70 hover:bg-primary/5 hover:text-primary"
      onClick={() => onCollapsedChange(!collapsed)}
    >
      <ChevronUp
        aria-hidden="true"
        className={cn(
          'size-3.5 transition-transform',
          collapsed && 'rotate-180'
        )}
      />
    </button>
  ) : null

  if (collapsed) {
    return (
      <div className="mx-5.5">
        <button
          type="button"
          aria-expanded={false}
          // The badge inside is hover-only: a focusable element inside a button is invalid.
          aria-label={`Expand the decision. ${headline.sentence}`}
          // w-full in a wrapper: a button shrinks to its content even as flex.
          className="flex w-full items-center gap-3 rounded-[12px] bg-sidebar px-4 py-2.5 text-left transition-colors hover:bg-primary/[0.06]"
          onClick={() => onCollapsedChange?.(false)}
        >
          <DecisionChip lead={lead} selected={form.disposition} />
          <div className="ml-auto flex shrink-0 items-center gap-3">
            {needsAttention ? saveState : null}
            <ChevronUp
              aria-hidden="true"
              className="size-3.5 rotate-180 text-primary/70"
            />
          </div>
        </button>
      </div>
    )
  }

  return (
    <div className="mx-5.5 rounded-[12px] bg-sidebar p-4">
      <div className="flex items-start justify-between gap-3">
        <Typography
          variant="body"
          className="text-sm font-normal leading-normal text-primary"
        >
          <span className="font-bold">{headline.sentence}</span>
          {headline.tail && <span> {headline.tail}</span>}
        </Typography>
        {fold}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-1.75">
        {choices.map((choice) => {
          const isRecorded = recorded === choice
          const isProposed = !isRecorded && form.disposition === choice

          return (
            <Button
              key={choice}
              type="button"
              variant="outline"
              aria-pressed={isRecorded || isProposed}
              disabled={disabled}
              className={cn(
                'h-11 gap-1.5 rounded-[9px] text-sm [&_svg]:size-2.75',
                isRecorded
                  ? CHOICE_PAINT[choice].recorded
                  : isProposed
                    ? CHOICE_PAINT[choice].proposed
                    : CHOICE_PAINT[choice].idle
              )}
              onClick={() => {
                setTouched(true)
                setForm((current) => ({ ...current, disposition: choice }))
              }}
            >
              {isProposed && <Sparkles aria-hidden="true" />}
              {dispositionLabel(choice)}
            </Button>
          )
        })}
      </div>

      {reasonRequired && (
        <div className="mt-2.5">
          <Typography
            variant="body-sm"
            className="mb-1.25 leading-[1.4] text-primary/75"
          >
            A {dispositionLabel(form.disposition ?? 'PASS')} needs a reason
          </Typography>
          <Select
            value={effectiveReason ?? undefined}
            disabled={disabled}
            onValueChange={(value) => {
              setTouched(true)
              setForm((current) => ({ ...current, reason: value }))
            }}
          >
            <SelectTrigger
              className={FIELD_CHROME}
              aria-label="Reason"
              aria-invalid={touched && missingReason}
            >
              <SelectValue placeholder="Choose a reason" />
            </SelectTrigger>
            <SelectContent>
              {(options.data?.passReasons ?? []).map((reason) => (
                <SelectItem key={reason.value} value={reason.value}>
                  {reason.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Collapsible open={detailOpen} onOpenChange={setDetailOpen}>
        <div className="mt-3 flex items-center justify-between gap-3">
          <CollapsibleTrigger className="group/detail -mx-1.5 -my-1 flex items-center gap-1.25 rounded-md px-1.5 py-1 text-primary/75 hover:bg-primary/5">
            <ChevronRight
              aria-hidden="true"
              className="size-3 transition-transform group-data-[state=open]/detail:rotate-90"
            />
            <Typography as="span" variant="body-sm">
              Note and add-on
            </Typography>
          </CollapsibleTrigger>
          {saveState}
        </div>

        <CollapsibleContent className="mt-2.5 flex flex-col gap-2">
          <Textarea
            aria-label="Commentary"
            rows={2}
            placeholder="Note (optional)"
            className="w-full rounded-lg border-primary/55 bg-white px-2.5 py-2 text-sm"
            value={form.commentary}
            disabled={disabled}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                commentary: event.target.value,
              }))
            }
            onBlur={() => {
              setCommittedCommentary(form.commentary)
              setTouched(true)
            }}
          />
          {/* Shown, not pre-filled: a seeded note would be saved as the reviewer's words. */}
          {form.commentary.length === 0 && lead.recommendationReason && (
            <Typography
              variant="body-sm"
              className="flex gap-1.5 text-xs italic leading-[1.4] text-primary/70"
            >
              <Sparkles
                aria-hidden="true"
                className="mt-0.75 size-2.75 shrink-0 text-secondary"
              />
              <span>Screening: {lead.recommendationReason}</span>
            </Typography>
          )}
          <Select
            value={form.addOn}
            disabled={disabled}
            onValueChange={(value) => {
              if (
                value !== ADD_ON_VALUES.yes &&
                value !== ADD_ON_VALUES.no &&
                value !== ADD_ON_VALUES.unset
              ) {
                return
              }
              setTouched(true)
              setForm((current) => ({ ...current, addOn: value }))
            }}
          >
            <SelectTrigger
              className="w-full bg-white"
              aria-label="Add-on candidate"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ADD_ON_VALUES.unset}>
                Add-on: not answered
              </SelectItem>
              <SelectItem value={ADD_ON_VALUES.yes}>Add-on: yes</SelectItem>
              <SelectItem value={ADD_ON_VALUES.no}>Add-on: no</SelectItem>
            </SelectContent>
          </Select>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function DecisionChip({
  lead,
  selected,
}: {
  lead: DealLeadDetail
  selected: SaveableDisposition | null
}) {
  const recorded = lead.disposition === 'UNREVIEWED' ? null : lead.disposition
  const shown = saveableOrNull(recorded ?? undefined) ?? selected

  if (shown === null) {
    if (lead.disposition === 'UNREVIEWED') return null

    return (
      <Typography as="span" variant="body-sm" className="text-primary/75">
        {dispositionLabel(lead.disposition)}
      </Typography>
    )
  }

  const isRecorded = recorded === shown
  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs',
        isRecorded ? CHOICE_PAINT[shown].recorded : CHOICE_PAINT[shown].proposed
      )}
    >
      {!isRecorded && <Sparkles aria-hidden="true" className="size-3" />}
      {dispositionLabel(shown)}
    </span>
  )

  if (isRecorded) return badge

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-72">
          <RecommendationSummary
            summary={`Screening suggests ${dispositionLabel(shown)}`}
            reason={lead.latestScreeningRun?.recommendationReason}
            footnote="Nothing is recorded until you choose."
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function SaveState({
  lead,
  pending,
  unsaved,
  missingReason,
  selected,
}: {
  lead: DealLeadDetail
  pending: boolean
  unsaved: boolean
  missingReason: boolean
  selected: SaveableDisposition | null
}) {
  if (!lead.capabilities.canDecide) {
    return (
      <div className="flex items-center gap-1.5">
        <AlertTriangle
          aria-hidden="true"
          className="size-3.5 shrink-0 text-amber-700"
        />
        <Typography as="span" variant="body-sm" className="text-primary/75">
          Cannot be decided right now
        </Typography>
      </div>
    )
  }

  const label = pending
    ? 'Saving…'
    : unsaved
      ? missingReason
        ? 'Not saved — choose a reason'
        : 'Not saved'
      : lead.disposition !== 'UNREVIEWED'
        ? lead.decisionComparison?.followedRecommendedDisposition === false
          ? 'Saved, overriding the suggestion'
          : 'Saved'
        : // Off what is lit, not whether a run exists: a Needs information run lights nothing.
          selected === null
          ? 'Nothing recorded'
          : 'Pre-selected by screening'

  return (
    <Typography
      as="span"
      variant="body-sm"
      className={unsaved ? 'text-amber-900' : 'text-primary/75'}
    >
      {label}
    </Typography>
  )
}
